// ─── The ONE notification warning rule (Ben's rulings, 2026-09-17) ────────────────────────────
//
// Until booking notification emails exist, ASSUME EVERY STUDENT AND CLIENT HAS BEEN NOTIFIED. The
// board and the detail panel therefore stopped saying "notified" at all — a "✓ notified" label
// derived from a flag nobody sets by telling anybody was an assertion the system could not make.
// What is left is a WARNING, and only a warning: an amber phone symbol meaning "somebody has to
// ring this client".
//
// It fires for two reasons:
//   modified  — the booking has a notifiable change since the client was told (the server decides
//               what is notifiable: start/end moved, or the aircraft TYPE changed) and nobody has
//               recorded advising them.
//   no_email  — a student or client on the booking has no usable email address, and notification
//               will rely on email.
//
// ⚠ THE CHIP AND THE PANEL BOTH READ THIS FUNCTION, WHICH IS WHY IT EXISTS. Two renderings of one
// rule drift, and a warning that shows in one place and not the other is worse than neither: the
// operator learns the board cannot be trusted.
//
// Student swap and cancellation warnings are deliberately NOT here — they belong with the 0.12.8
// student notification system.

import { formatPersonName } from './opsBoard/chipChannels';
import type { Booking } from './opsBoardTypes';

export type NotifyWarning =
  | null
  | { kind: 'modified' }
  | { kind: 'no_email'; names: string[] };

/** The people on a booking with no usable email address, in participant order. */
export function noEmailParticipants(b: Booking): Booking['participants'] {
  // ⚠ `=== true`, NOT truthiness on an optional. A provisional row built offline
  // (lib/offline/overlay.ts) carries no email_missing at all, and an unknown is NOT a warning —
  // inventing one on a queued booking would cry wolf on every offline create.
  return (b.participants ?? []).filter((p) => p.email_missing === true);
}

/** The display name for a no-email warning: the person's name, or their external name. */
export function participantWarningName(p: Booking['participants'][number]): string {
  return formatPersonName(p.user) ?? (p.external_name?.trim() || 'this person');
}

export function notifyWarning(b: Booking): NotifyWarning {
  // A cancelled booking is not happening, so there is nobody to ring about it. This is the only
  // blanket exclusion left.
  if (b.booking_status === 'cancelled') return null;

  // ⚠ A WAITLISTED BOOKING WITH THE PEN *IS* SOMEBODY TO RING (Ben, 2026-09-18). This used to read
  // `if (cancelled || b.waitlisted) return null`, on the reasoning that a waitlisted booking "has
  // no time to be told about yet". That was wrong in the one case that matters most: a MAINTENANCE
  // DISPLACEMENT both waitlists the booking AND sets the pen, so the client's lesson has just been
  // taken away from them — the single loudest reason to pick up the phone — and the board said
  // nothing and counted nothing. Worse, the detail panel DID show the amber line (it reads
  // modified_after_notify directly), so the panel and the board disagreed, which is exactly the
  // drift this shared helper exists to prevent.
  //
  // So the pen is checked BEFORE the waitlist state, and a displaced booking warns like any other.
  if (b.modified_after_notify && !b.modify_notified_at) return { kind: 'modified' };

  // …but being waitlisted is NOT by itself a reason to ring, and neither is a missing address on a
  // booking that holds no agreed time. Ben's ruling covers the pen only, so everything below this
  // line stays as it was: an ordinary unallocated booking is a scheduling job, not a phone call.
  if (b.waitlisted) return null;

  // BOTH can apply. `modified` wins for the single-symbol surfaces (the chip has one badge); the
  // panel has room and shows both lines, which is why it calls noEmailParticipants separately.
  const missing = noEmailParticipants(b);
  if (missing.length > 0) return { kind: 'no_email', names: missing.map(participantWarningName) };
  return null;
}

/** The reason, in the words an operator would use. Feeds the chip badge's SVG <title>. */
export function notifyWarningText(w: NotifyWarning): string {
  if (!w) return '';
  if (w.kind === 'modified') return 'Changed since the client was told';
  return `No email address for ${w.names.join(', ')}`;
}
